// SYSTEM_ROUTE: /focus
import { useEffect, useRef, useState } from 'react';
import type { FocusSession, FocusSummary } from '@life-os/app-core';
import { FOCUS_PRESETS } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

function mmss(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function weekRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 6);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

function todayRange(): { from: string; to: string } {
  const d = new Date().toISOString().slice(0, 10);
  return { from: d, to: d };
}

export function FocusScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();

  const [planned, setPlanned] = useState(25);
  const [remaining, setRemaining] = useState(25 * 60);
  const [running, setRunning] = useState(false);
  const [note, setNote] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sessions = useAsync<readonly FocusSession[]>(() => services.focus.list(), [revision]);
  const week = useAsync<FocusSummary>(() => { const r = weekRange(); return services.focus.summary(r.from, r.to); }, [revision]);
  const today = useAsync<FocusSummary>(() => { const r = todayRange(); return services.focus.summary(r.from, r.to); }, [revision]);

  // Tick the countdown while running.
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) return 0;
        return r - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [running]);

  const logCompleted = async (minutes: number) => {
    await services.focus.logSession({ minutes, note });
    setNote('');
    refresh();
  };

  // When the countdown reaches zero while running, log the full planned session.
  useEffect(() => {
    if (running && remaining === 0) {
      setRunning(false);
      void logCompleted(planned);
      setRemaining(planned * 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, running]);

  const choosePreset = (m: number) => {
    setPlanned(m);
    setRemaining(m * 60);
    setRunning(false);
  };

  const reset = () => {
    setRunning(false);
    setRemaining(planned * 60);
  };

  const finishEarly = async () => {
    const elapsed = Math.max(1, Math.round((planned * 60 - remaining) / 60));
    setRunning(false);
    setRemaining(planned * 60);
    await logCompleted(elapsed);
  };

  const recent = [...(sessions.value ?? [])].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn)).slice(0, 12);

  return (
    <>
      <ScreenHeader title={<>Focus & Pomodoro</>} subtitle={<>Run a focused block, then log it. Sessions are proof of execution — gentle evidence of effort,
            not a clock watching you.</>} actions={<>{today.value ? (
          <div className="lc-row__meta">
            <Chip tone="primary">{today.value.totalMinutes} min today</Chip>
          </div>
        ) : null}</>} />

      <Pane title="Timer">
        <Stack>
          <div style={{ textAlign: 'center', fontSize: '3rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: 1 }}>
            {mmss(remaining)}
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            {FOCUS_PRESETS.map((m) => (
              <Button key={m} variant={planned === m ? 'primary' : 'ghost'} onClick={() => choosePreset(m)}>
                {m}m
              </Button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', justifyContent: 'center' }}>
            {!running ? (
              <Button variant="primary" onClick={() => setRunning(true)} disabled={remaining === 0}>
                {remaining === planned * 60 ? 'Start' : 'Resume'}
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setRunning(false)}>
                Pause
              </Button>
            )}
            <Button variant="ghost" onClick={reset}>
              Reset
            </Button>
            <Button variant="ghost" onClick={() => void finishEarly()} disabled={remaining === planned * 60}>
              Finish & log
            </Button>
          </div>
          <input
            className="lc-cmdbar"
            value={note}
            placeholder="What are you focusing on? (optional)"
            onChange={(e) => setNote(e.target.value)}
            aria-label="Focus note"
          />
        </Stack>
      </Pane>

      {week.value && week.value.sessionCount > 0 ? (
        <Pane title="This week" hint={`${week.value.daysFocused} day${week.value.daysFocused === 1 ? '' : 's'}`}>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-md)', flexWrap: 'wrap' }}>
            <Chip tone="primary">{Math.round((week.value.totalMinutes / 60) * 10) / 10} h focused</Chip>
            <Chip tone="neutral">{week.value.sessionCount} sessions</Chip>
          </div>
        </Pane>
      ) : null}

      <Pane title="Recent sessions" hint={`${recent.length}`}>
        <Stack>
          {recent.length === 0 ? (
            <EmptyState title="No sessions yet">Pick a length, press start, and do one focused block.</EmptyState>
          ) : (
            recent.map((s) => (
              <CompactRow
                key={String(s.id)}
                title={`${s.minutes} min`}
                subtitle={`${s.occurredOn.slice(0, 10)}${s.note ? ` · ${s.note}` : ''}`}
              />
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
